import React from "react";
import {PageLayout} from "../../Layouts/PageLayout";
import {
    Bullseye,
    Button, EmptyState, EmptyStateBody, EmptyStateIcon, EmptyStateVariant,
    Grid,
    GridItem,
    Pagination, Select, SelectOption, TextInput, Title,
    ToggleGroup,
    ToggleGroupItem
} from "@patternfly/react-core";
import {PerPageOptions} from "@patternfly/react-core/dist/esm/components/Pagination/Pagination";
import { ThLargeIcon, ListIcon, SyncIcon, DownloadIcon, SearchIcon  } from '@patternfly/react-icons';
import {useAuthState} from "../../Context/AuthProvider/AuthProvider";
import {DashboardTableItems} from "./DashboardTableItems";
import {DashboardGalleryItems} from "./DashboardGalleryItems";

import {useAlertDispatcherState} from "../../Context/AlertProvider/AlertProvider";
import {addAlert} from "../../Context/AlertProvider/AlertProviderActions";
import {DiazarModalDownloads} from "./DiazarModalDownloads";
import {useItemsDispatcherState, useItemsState} from "../../Context/ItemsProvider/ItemsProvider";
import {getItems} from "../../Context/ItemsProvider/ItemsProviderActions";
import {GalleryItemData, orderData} from "../../Context/ItemsProvider/ItemsProviderDomain";

const paginationPageSize = [10, 20, 50, 100];

export const Dashboard: React.FunctionComponent = (_props) => {
    const userDetails = useAuthState()
    const itemsDispatcher = useItemsDispatcherState()
    const {items, isLoading, itemsMessage, itemsMessageVariant} = useItemsState()

    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false)
    const [filteresItems, setFilteresItems] = React.useState<GalleryItemData[]>( [] )
    const [orderedItems, setOrderedItems] = React.useState<GalleryItemData[]>( [] )
    const [paginatedItems, setPaginatedItems] = React.useState<GalleryItemData[]>( [] )
    const [searchPhrase, setSearchPhrase] = React.useState<string>("")

    const [pageNumber, setPageNumber] = React.useState<number>(1);
    const [perPageNumber, setPerPageNumber] = React.useState<number>(20);

    const [isCadSelected, setIsCadSelected] = React.useState<boolean>( true )

    const [order, setOrder] = React.useState<number>(0)
    const [isOpenSelectOrder, setIsOpenSelectOrder] = React.useState<boolean>( false )

    const [selectedItems, setSelectedItems] = React.useState<string[]>([])

    const alertDispatch = useAlertDispatcherState()

    const onToggleSelectOrder = React.useCallback( (isOpen:boolean)=>{
        setIsOpenSelectOrder( isOpen )
    }, [] )

    const onSelectOrder = React.useCallback( (selection:number)=>{
        setOrder( selection)
        setIsOpenSelectOrder( false )
    }, [] )

    const onSetPage =  React.useCallback( (_event:any, pageNumber:number)=>{
        setPageNumber(pageNumber)
    }, [] )

    const onPerPageSelect = React.useCallback( (_event:any, perPage:number)=>{
        setPageNumber( 1 )
        setPerPageNumber( perPage )
    }, [] )

    const onSelectItem = React.useCallback( (newId:string) => {
        const index = selectedItems.indexOf( newId )

        if( index >= 0 ){
            setSelectedItems( selectedItems.filter( itemId => itemId !== newId ) )
        }else{
            setSelectedItems( [...selectedItems, newId] )
        }
    }, [selectedItems] )

    const paginationPages:PerPageOptions[] = React.useMemo( ()=>{
        let sizes:PerPageOptions[] = []

        paginationPageSize
            .filter( value => value < filteresItems.length )
            .map( value => { sizes.push( { title:`${value}`, value:value } ); return null; } )

        sizes.push( { title:`${ filteresItems.length }`, value:filteresItems.length } )

        const firstPerPageNumber = (sizes[0].value) ? sizes[0].value : paginationPageSize[0]
        setPerPageNumber(  firstPerPageNumber  )

        return sizes
    }, [filteresItems.length] )

    const description = (
            <React.Fragment>
                <p>
                    Selecione los elementos a descargar para poder continuar...
                </p>
                <Grid hasGutter sm={12} >
                    <GridItem sm={2} xl={1} >
                        <ToggleGroup>
                            <ToggleGroupItem icon={<ThLargeIcon />}
                                             isSelected={ isCadSelected }
                                             onClick={ ()=>{ setIsCadSelected(true) } }
                                             aria-label="card icon button"
                            />
                            <ToggleGroupItem icon={<ListIcon />}
                                             isSelected={ !isCadSelected }
                                             onClick={ ()=>{ setIsCadSelected(false) } }
                                             aria-label="table icon button"
                            />
                        </ToggleGroup>
                    </GridItem>
                    <GridItem sm={5} xl={4} xl2={3}>
                            <TextInput iconVariant="search"
                                       value={ searchPhrase }
                                       onChange={ (value)=>{ setSearchPhrase(value) } }
                                       aria-label="text input Filter"
                            />
                    </GridItem>
                    <GridItem sm={5} xl={4} xl2={3}>
                        <Select placeholderText={ orderData[0] }
                                onToggle={ onToggleSelectOrder }
                                isOpen={ isOpenSelectOrder }
                                onSelect={ (_e, value) =>{
                                    onSelectOrder( value as number )
                                } }
                                selections={ order }
                        >
                            {
                                orderData.map( (val, index) => <SelectOption key={index} value={index}>{ val }</SelectOption> )
                            }
                        </Select>
                    </GridItem>
                </Grid>


            </React.Fragment>
        )

    const hasDownloads = React.useMemo( ()=>{
        return selectedItems.length > 0
    }, [selectedItems.length] )

    React.useEffect( ()=>{
        let orderArray = [...filteresItems]
        if( order === 0 ){
            setOrderedItems( orderArray )
        }else{
            setOrderedItems( orderArray.sort((a, b) => a.sort( b, order ) ) )
        }
    }, [order, filteresItems] )

    React.useEffect( ()=>{
        if( searchPhrase.trim() === '' ){
            setFilteresItems( items )
        }else{
            setFilteresItems( items.filter(
                value => (
                    value.name.toLowerCase().includes(searchPhrase.toLowerCase()) ||
                    value.file_description.toLowerCase().includes(searchPhrase.toLowerCase())
                )
            ) )
        }
    }, [searchPhrase, items] )

    React.useEffect( ()=>{
        const start = (pageNumber - 1) * perPageNumber
        const end = start + perPageNumber

        setPaginatedItems( orderedItems.slice( start, end ) )
    }, [orderedItems, perPageNumber, pageNumber]  )

    const getAllItems = React.useCallback(async () => {
        const token = (userDetails.token) ? userDetails.token : ''
        setSelectedItems([])
        getItems(token, itemsDispatcher).then()
    }, [userDetails.token, itemsDispatcher] )

    React.useEffect(  () => {
        getAllItems().then()
    }, [getAllItems] )

    React.useEffect(  ()=>{
        if( itemsMessage !== undefined && itemsMessage.trim() !== '' ){
            const variant = (itemsMessageVariant) ? itemsMessageVariant : 'default'

            if( variant === "danger"){
                addAlert( 'Error al hacer la petición', itemsMessage,variant, alertDispatch )
            }else{
                addAlert( 'Información:', itemsMessage,variant, alertDispatch )
            }
        }
    }, [itemsMessage, itemsMessageVariant, alertDispatch] )

    return (
        <PageLayout title="Dashboard" description={description}>
            <DiazarModalDownloads items={ items }
                                  selectedIds={ selectedItems }
                                  isModalOpen={isModalOpen}
                                  setIsModalOpen={ setIsModalOpen }
                                  onClickItem={ onSelectItem }/>
            <Pagination
                itemCount={ orderedItems.length }
                perPage={ perPageNumber }
                page={ pageNumber }
                onSetPage={ onSetPage }
                widgetId="pagination-options-menu-top"
                onPerPageSelect={ onPerPageSelect }
                titles={{
                    perPageSuffix: 'Items por página',
                    ofWord: 'de'
                }}
                perPageOptions={paginationPages}
                style={{marginBottom:'15px'}}
                //isSticky
            >
                <Button title="Sincronizar"
                        onClick={ ()=>{ getAllItems().then() } }
                        icon={ <SyncIcon /> }
                        isDisabled={ isLoading }
                />
                <Button title="Descargar"
                        isDisabled={ !hasDownloads }
                        icon={ <DownloadIcon /> }
                        onClick={ ()=>{ setIsModalOpen( prev => !prev ) } }
                >
                    { (selectedItems.length > 0) ? selectedItems.length : '' }
                </Button>
            </Pagination>
            {
                ( paginatedItems.length === 0 ) ?
                    (
                        <Bullseye>
                            <EmptyState variant={EmptyStateVariant.small}>
                                <EmptyStateIcon icon={SearchIcon} />
                                <Title headingLevel="h2" size="lg">
                                    No hay coincidencias
                                </Title>
                                {
                                    ( items.length === 0 ) ?
                                        <React.Fragment>
                                            <EmptyStateBody>No tiene Archivos asociados.</EmptyStateBody>
                                        </React.Fragment>
                                        :
                                        <React.Fragment>
                                            <EmptyStateBody>Limpie los filtros e intentelo nuevamente.</EmptyStateBody>
                                            <Button variant="link" onClick={ ()=>{ setSearchPhrase('') } }>Limpiar filtros</Button>
                                        </React.Fragment>
                                }

                            </EmptyState>
                        </Bullseye>
                    ) :
                (isCadSelected) ?
                    <DashboardGalleryItems items={ paginatedItems } selectedItems={ selectedItems } onClickItem={ onSelectItem } /> :
                    <DashboardTableItems items={ paginatedItems } selectedItems={ selectedItems } onClickItem={ onSelectItem } />
            }
        </PageLayout>
    )
}